import { LoadingScreen } from 'component/banner';
import { Container, HorizBox, Pad, PadBox, WindowTitle } from 'component/basics';
import { CTAButton, TextButton } from 'component/button';
import { useServersideConstructor } from 'component/constructor';
import { addNotificationToast } from 'component/notification-toast';
import { ConversationScreen } from 'component/screen';
import { useTranslation } from 'component/translation';
import React, { useState } from 'react';
import { View } from 'react-native';
import { Catcher } from 'system/catcher';
import { useAppData } from 'util/appdataContext';
import { useDatastore, useGlobalProperty, useInstanceKey, usePersonaKey, usePersonaObject } from 'util/datastore';
import { logEventAsync } from 'util/eventlog';
import { useConfig } from 'util/features';
import type { Structure, TLabel } from 'util/stdtypes';
import { t } from 'util/stdtypes';
import { removeUndefinedFields } from 'util/util';

export type ProfileModule = {
    label: TLabel;
    key: string;
    view: React.ComponentType;
    edit?: React.ComponentType;
    checkForErrors?: (args: any) => Promise<Record<string, any> | null>;
    makePreview?: (args: any) => Record<string, any>;
};

export type ProfileConfig = {
    profileWidgets: React.ComponentType[];
    profileModules: ProfileModule[];
};

export const ProfileStructure: Structure<ProfileConfig> = {
    key: 'profile',
    name: 'User Profile',
    screen: ProfileScreen,
    hasFocusTrap: true,
    receivesConfigDefaults: {
        profileWidgets: [],
        profileModules: [],
    },
};

function ProfileScreen() {
    const userId = useInstanceKey();
    const initialized = useServersideConstructor();
    const userObject = usePersonaObject(userId);
    const userName = userObject?.name;
    const photoUrl = userObject?.photoUrl;
    const { profileWidgets, profileModules } = useConfig<ProfileConfig>();

    if (!initialized) return <LoadingScreen label={t('Loading profile...')} />;
    if (!userName) return <LoadingScreen label={t('No such user')} />;

    return (
        <ConversationScreen>
            <WindowTitle text={userName} />
            <Pad />
            <PadBox bottom={40} horiz={20}>
                {profileModules.map((module, i) => (
                    <Catcher key={i}>
                        <ProfileModuleHolder module={module} />
                        <Pad size={20} />
                    </Catcher>
                ))}
                {profileWidgets.map((Widget, i) => (
                    <Catcher key={i}>
                        <Widget />
                    </Catcher>
                ))}
            </PadBox>
        </ConversationScreen>
    );
}

type FieldEditContextType = {
    updates: Record<string, any>;
    updateField: (key: string, value: any) => void;
    errors: Record<string, any>;
};
export const FieldEditContext = React.createContext<FieldEditContextType>({
    updates: {},
    updateField: () => {},
    errors: {},
});

// FIXME: This function is too large and should be broken up
/* eslint-disable-next-line max-lines-per-function */
export function ProfileModuleHolder({ module }) {
    const [editing, setEditing] = useState(false);
    const personaKey = usePersonaKey();
    const instanceKey = useInstanceKey();
    const isMyProfile = personaKey === instanceKey;
    const tLabel = useTranslation(module.label);
    const oldState = useGlobalProperty('fields') ?? {};
    const [updates, setUpdates] = useState(oldState);
    const [errors, setErrors] = useState({});
    const [inProgress, setInProgress] = useState(false);
    const datastore = useDatastore();
    const appData = useAppData();

    async function onSave() {
        setInProgress(true);
        const { checkForErrors, makePreview } = module;
        if (checkForErrors) {
            const newErrors = await checkForErrors({ datastore, updates });
            if (newErrors && Object.keys(newErrors).length > 0) {
                setErrors(newErrors);
                setInProgress(false);
                return;
            }
        }
        let preview: Record<string, any> | null = null;
        if (makePreview) {
            preview = removeUndefinedFields(makePreview({ updates }));
        }
        await datastore.callServerAsync('profile', 'update', {
            updates,
            preview,
            moduleKey: module.key,
        });
        setInProgress(false);
        setEditing(false);
        addNotificationToast({
            appData,
            label: t('Your profile has been saved!'),
            type: 'success',
            duration: 5000,
        });
        setErrors({});
        logEventAsync(datastore, 'profile-edit', { preview });
    }

    function onCancel() {
        setEditing(false);
        setErrors({});
        setUpdates(oldState ?? {});
    }

    if (editing) {
        return (
            <Container>
                <WithEditableFields errors={errors} setUpdates={setUpdates} updates={updates}>
                    <PadBox horiz={20} vert={20}>
                        {React.createElement(module.edit, { updates, setUpdates, errors })}
                        <Pad size={24} />
                        <HorizBox center>
                            <View aria-live='polite'>
                                {inProgress ? (
                                    <CTAButton disabled label={t('Saving...')} />
                                ) : (
                                    <CTAButton label={t('Save')} onPress={onSave} />
                                )}
                            </View>
                            <Pad />
                            <TextButton label={t('Cancel')} onPress={onCancel} />
                        </HorizBox>
                    </PadBox>
                </WithEditableFields>
            </Container>
        );
    } else {
        return (
            <View>
                <WithEditableFields errors={errors} setUpdates={setUpdates} updates={updates}>
                    {React.createElement(module.view)}
                    <Pad size={24} />
                    {isMyProfile && module.edit && (
                        <CTAButton
                            formatParams={{ tLabel }}
                            label={t('Edit {tLabel}')}
                            onPress={() => setEditing(true)}
                            size='compact'
                            type='secondary'
                        />
                    )}
                </WithEditableFields>
            </View>
        );
    }
}

export function WithEditableFields({ children, updates, setUpdates, errors }) {
    // HACK
    let pendingUpdates = {};
    function updateField(key, value) {
        pendingUpdates = { ...pendingUpdates, [key]: value };
        setUpdates({ ...updates, ...pendingUpdates });
    }

    return <FieldEditContext.Provider value={{ updates, updateField, errors }}>{children}</FieldEditContext.Provider>;
}

export function useEditableField(key, defaultValue) {
    const { updates, updateField } = React.useContext(FieldEditContext);
    if (!updates) throw new Error('useEditableField must be used within a WithEditableFields');
    const value = updates?.[key];
    return [value === undefined ? defaultValue : value, (value) => updateField(key, value)];
}

export function useProfileField(key, defaultValue) {
    const fields = useGlobalProperty('fields');
    return fields?.[key] ?? defaultValue;
}

export function useEditErrors() {
    const { errors } = React.useContext(FieldEditContext);
    return errors;
}
